import React, { useMemo, useState } from 'react'
import {
  createColumnHelper,
  Table,
  Link,
  Box,
  Text,
  Button,
  Message,
  Flex,
} from '@workwhile/ui'
import { Work } from 'api/typings'
import moment from 'moment/moment'
import WorkerDetail from 'components/Shifts/Workers/WorkerDetail'
import api from 'api'
import {
  ReviewShiftsProvider,
  useReviewShiftsContextValue,
} from './context/ReviewShiftsProvider'
import { useAdminReviewShifts } from 'queries/review'
import { ReviewFilters } from './components/ReviewFilters/ReviewFilters'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6'
import { ShiftStatusRender } from 'pages/UnpaidShifts/components/UnpaidShiftsRenderers/ShiftStatusRender'
import { TimelineTripModal } from './components/TimelineTripModal/TimelineTripModal'
import { AgentReasoningModal } from './components/AgentReasoningModal/AgentReasoningModal'
import { WorkBreaksButton } from './components/WorkBreaksButton/WorkBreaksButton'
import { getLocalTimezone } from 'lib/time'

const columnHelper = createColumnHelper<Work>()

function AdminReviewQueueContent() {
  const {
    state: { data, pagination },
    actions: { refetch, setPagination },
  } = useReviewShiftsContextValue()

  const [selectedWork, setSelectedWork] = useState<Work | null>(null)
  const [selectedReasoning, setSelectedReasoning] = useState<string | null>(
    null
  )

  const onActionSuccess = (action: string) => {
    if (action === 'mark_approved') {
      refetch()
    }
  }

  const removeShiftFromReviewList = (workId: number) => {
    // Refetch to update the list
    refetch()
  }

  const rejectAdminReview = (work: Work) => {
    api.post(`/admin/work/${work.id}/admin_review_reject`).then(() => {
      alert(
        `Rejected review for assoc(${work.id})/user(${work.worker?.id}). Worker will be blocked and all shifts will be removed.`
      )
      refetch()
    })
  }

  const getActionsView = (work: Work) => {
    let rejectButton: React.ReactNode | null = null
    if (
      work.adminReview &&
      (work.adminReview.workerClaimsTheyWorked ||
        work.adminReview.workerSubmittedDocsForBail)
    ) {
      rejectButton = (
        <Button mt={3} onClick={() => rejectAdminReview(work)}>
          Reject review & Block worker
        </Button>
      )
    }
    return (
      <>
        <Flex gap={2} mb={2}>
          <Button
            variant="secondary"
            size="small"
            onClick={() => setSelectedWork(work)}
          >
            View Timeline & Trip
          </Button>
        </Flex>
        {work.id !== undefined && work.shift?.id !== undefined ? (
          <WorkBreaksButton
            shiftId={work.shift.id}
            workId={work.id}
            timezone={
              work.shift.location?.address.timezone ?? getLocalTimezone()
            }
            clockedInAt={work.startedAt}
            clockedOutAt={work.completedAt}
            onEditClose={refetch}
          />
        ) : null}
        <WorkerDetail
          shift={work.shift}
          workId={work.id}
          workerId={work.worker?.id}
          onActionSuccess={onActionSuccess}
          removeShiftFromReviewList={() =>
            work.id && removeShiftFromReviewList(work.id)
          }
        />
        {rejectButton}
      </>
    )
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'Work ID',
        enableSorting: false,
      }),
      columnHelper.accessor('worker', {
        header: 'Worker',
        cell: ({ row }) => {
          const userId = row.original.worker?.id
          const userName = row.original.worker?.name
          const displayName = userName || ''
          return (
            <Text>
              {displayName ? `${displayName} (` : ''}
              <Link to={`/users/${userId}`} target="_blank">
                {userId}
              </Link>
              {displayName ? ')' : ''}
            </Text>
          )
        },
      }),
      columnHelper.accessor('shift', {
        header: 'Shift Info',
        enableSorting: false,
        cell: (info) => {
          const cell = info.getValue()
          const leadEnteredTime = info.row.original.leadEnteredTime
          const shiftId = cell?.id
          const positionId = cell?.position?.id
          const positionName = cell?.position?.name
          const companyId = cell?.company?.id
          const companyName = cell?.company?.name

          return (
            <Flex flexDirection="column" gap={1}>
              <Text>
                <Text as="span" fontWeight="bold">
                  Shift:
                </Text>{' '}
                {shiftId ? (
                  <Link to={`/shifts/${shiftId}`} target="_blank">
                    #{shiftId}
                  </Link>
                ) : (
                  'N/A'
                )}
              </Text>
              {positionId && positionName ? (
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Position:
                  </Text>{' '}
                  {positionName} (
                  <Link
                    to={`/position_editor?positionId=${positionId}`}
                    target="_blank"
                  >
                    #{positionId}
                  </Link>
                  )
                </Text>
              ) : null}
              {companyId && companyName ? (
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Company:
                  </Text>{' '}
                  {companyName} (
                  <Link to={`/companies/${companyId}`} target="_blank">
                    #{companyId}
                  </Link>
                  )
                </Text>
              ) : null}
              {cell?.startsAt && cell?.endsAt ? (
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Times:
                  </Text>{' '}
                  {moment(cell.startsAt).format('YYYY-MM-DD h:mm A')} -{' '}
                  {moment(cell.endsAt).format('YYYY-MM-DD h:mm A')}
                </Text>
              ) : null}
              {cell?.lunchLength !== undefined && cell?.lunchLength !== null ? (
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Lunch (mins):
                  </Text>{' '}
                  {cell.lunchLength}
                </Text>
              ) : null}
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Shift lead entered worker&apos;s time?
                </Text>
              </Text>
              <Text fontSize="sm">{leadEnteredTime ? 'Yes' : 'No'}</Text>
              {cell?.supervisors && cell.supervisors.length > 0 ? (
                <>
                  <Text fontSize="sm">
                    <Text as="span" fontWeight="bold">
                      Supervisor(s):
                    </Text>
                  </Text>
                  {cell.supervisors.map((supervisor) => (
                    <Text key={supervisor.id} fontSize="sm">
                      {supervisor.name} (
                      <Link to={`/users/${supervisor.id}`} target="_blank">
                        {supervisor.id}
                      </Link>
                      ). Phone: {supervisor.phoneNumber || 'N/A'}
                    </Text>
                  ))}
                </>
              ) : null}
            </Flex>
          )
        },
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const value = info.getValue()
          return value ? <ShiftStatusRender value={value} /> : null
        },
      }),
      columnHelper.accessor('adminReviewDetail', {
        header: 'Detail',
        size: 200,
        cell: (info) => {
          const row = info.row.original
          const cell = info.getValue()
          return (
            <Box maxWidth={200}>
              {row.adminReview?.description ? (
                <p style={{ whiteSpace: 'pre-line' }}>
                  {row.adminReview.description}
                </p>
              ) : null}
              {/* Detail arrives newline-joined from the backend, one line per issue */}
              <p style={{ whiteSpace: 'pre-line' }}>{cell}</p>
              {row.associatedOffense &&
              row.associatedOffense.userExplanation ? (
                <p>Explanation: {row.associatedOffense.userExplanation}</p>
              ) : null}
              {row.adminReview && row.adminReview.workerSubmittedDocsForBail ? (
                <a
                  href={`https://console.cloud.google.com/storage/browser/outfielders/${row.worker?.id};tab=objects`}
                  target="_blank"
                  rel="noreferrer"
                >
                  See Docs
                </a>
              ) : null}
              {row.trackingIssueReason ? (
                <Message variant="warning" hideIcon mt={3}>
                  User's reason for outage:
                  <br />
                  {row.trackingIssueReason}
                </Message>
              ) : null}
            </Box>
          )
        },
      }),
      columnHelper.accessor('adminReviewAgent', {
        header: 'Agent Output and Recommendation',
        enableSorting: false,
        size: 280,
        cell: (info) => {
          const agent = info.getValue()
          if (!agent) {
            return <Text color="muted">—</Text>
          }
          return (
            <Flex flexDirection="column" gap={1} maxWidth={280}>
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Output status:
                </Text>{' '}
                {agent.agentOutputStatus ?? '—'}
              </Text>
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Confidence:
                </Text>{' '}
                {agent.agentOutputConfidence ?? '—'}
              </Text>
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Recommendation:
                </Text>{' '}
                {agent.agentOutputRecommendation ?? '—'}
              </Text>
              {agent.agentOutputReasoning ? (
                <Button
                  variant="secondary"
                  size="small"
                  mt={1}
                  onClick={() =>
                    setSelectedReasoning(agent.agentOutputReasoning ?? null)
                  }
                >
                  Show step-by-step agent reasoning
                </Button>
              ) : null}
            </Flex>
          )
        },
      }),
      columnHelper.accessor('id', {
        header: 'Actions',
        enableSorting: false,
        size: 350,
        cell: (info) => getActionsView(info.row.original),
      }),
    ],
    []
  )

  return (
    <>
      <Flex justifyContent="left" alignItems="center" mb={3}>
        <ReviewFilters />
      </Flex>
      <Table
        columns={columns}
        data={data.items}
        textVerticalAlign="top"
        enableSorting={true}
        defaultSort={[
          {
            id: 'completedAt',
            desc: false,
          },
        ]}
        alternateRowColor={true}
        serverPagination={{
          rowCount: data.totalRows,
          state: pagination,
          setState: setPagination,
        }}
      />
      {selectedWork ? (
        <TimelineTripModal
          open={!!selectedWork}
          onClose={() => setSelectedWork(null)}
          work={selectedWork}
        />
      ) : null}
      {selectedReasoning ? (
        <AgentReasoningModal
          open={!!selectedReasoning}
          onClose={() => setSelectedReasoning(null)}
          reasoning={selectedReasoning}
        />
      ) : null}
    </>
  )
}

export function AdminReviewQueue() {
  return (
    <NuqsAdapter>
      <ReviewShiftsProvider
        useQueryHook={(options) => {
          const queryResult = useAdminReviewShifts(options)
          return {
            data: queryResult.data,
            isPending: queryResult.isPending,
            isError: queryResult.isError,
            error: queryResult.error,
            dataUpdatedAt: queryResult.dataUpdatedAt,
            refetch: queryResult.refetch,
            isFetching: queryResult.isFetching,
          }
        }}
      >
        <AdminReviewQueueContent />
      </ReviewShiftsProvider>
    </NuqsAdapter>
  )
}
